import { useEffect, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    MenuItem, TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../services/auth/AuthContext';
import apiRequest from '../../../../../services/api/handler';
import downloadEnquiryExport from '../../../../../services/api/exportEnquiries';
import OverlayLoader from '../../../../../components/overlayLoader';
import { loanTypeMap } from '../utils';

const SECTIONS = [
    { value: 'all', label: 'All sections' },
    { value: 'pending', label: 'In Progress' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'unserviceable', label: 'Unserviceable' },
    { value: 'pushed_to_field', label: 'Sent to Field' },
];

const EMPTY_FILTERS = {
    loan_type_code: 'all',
    current_application_status: 'all',
    state: 'all',
    created_from: '',
    created_to: '',
};

export default function ExportModal({ open, onClose, initialFilters }) {
    const { setIsAuthenticated } = useAuth();
    const navigate = useNavigate();

    const [filters, setFilters] = useState({ ...EMPTY_FILTERS, ...initialFilters });
    const [states, setStates] = useState([]);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);

    // Reopening the modal after the section changed should show the section the
    // user is actually looking at, so the prefill is re-applied on each open
    // rather than only on mount.
    useEffect(() => {
        if (open) {
            setFilters({ ...EMPTY_FILTERS, ...initialFilters });
            setError(null);
        }
    }, [open, initialFilters]);

    useEffect(() => {
        apiRequest('/reseller/filters', 'GET', null, true)
            .then((response) => setStates(response?.states ?? []))
            .catch(() => setStates([]));
    }, []);

    const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

    const handleDownload = () => {
        setDownloading(true);
        setError(null);
        downloadEnquiryExport(filters, setIsAuthenticated, navigate)
            .then(() => {
                setDownloading(false);
                onClose();
            })
            .catch(() => {
                setDownloading(false);
                setError('Could not generate the report. Please try again.');
            });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <OverlayLoader open={downloading} text="Preparing your report..." />
            <DialogTitle>Download report</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <TextField
                        select
                        size="small"
                        label="Loan type"
                        value={filters.loan_type_code}
                        onChange={(e) => setFilter('loan_type_code', e.target.value)}
                    >
                        <MenuItem value="all">All loan types</MenuItem>
                        {Object.keys(loanTypeMap)
                            .filter((code) => code !== 'null')
                            .map((code) => (
                                <MenuItem key={code} value={code}>{code}</MenuItem>
                            ))}
                    </TextField>

                    <TextField
                        select
                        size="small"
                        label="Section"
                        value={filters.current_application_status}
                        onChange={(e) => setFilter('current_application_status', e.target.value)}
                    >
                        {SECTIONS.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        size="small"
                        label="State"
                        value={filters.state}
                        onChange={(e) => setFilter('state', e.target.value)}
                    >
                        <MenuItem value="all">All states</MenuItem>
                        {states.map((state) => (
                            <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        size="small"
                        type="date"
                        label="Created from"
                        InputLabelProps={{ shrink: true }}
                        value={filters.created_from}
                        onChange={(e) => setFilter('created_from', e.target.value)}
                    />

                    <TextField
                        size="small"
                        type="date"
                        label="Created to"
                        InputLabelProps={{ shrink: true }}
                        value={filters.created_to}
                        onChange={(e) => setFilter('created_to', e.target.value)}
                    />

                    {error && <Box color="error.main">{error}</Box>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setFilters(EMPTY_FILTERS)}>Reset</Button>
                <Button onClick={onClose} color="secondary">Cancel</Button>
                <Button onClick={handleDownload} variant="contained" disabled={downloading}>
                    Download CSV
                </Button>
            </DialogActions>
        </Dialog>
    );
}
